// Service
import { POSTFORMDATA, GET, POST } from "../../service/CRUDService";
// Constants
import EntityConstants from "../../configs/EntityConstants";
// Utils
import {
  SIGN_UP_FIELD_LIMITS,
  state,
  schema,
  schema3,
} from "./SignUpSideCurtains.utils";
// Third Party
import { Form, Formik } from "formik";
import { toast } from "react-hot-toast";
import { useContext, useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import AuthContext from "../../store/auth-context";
import AppConstants from "../../configs/AppConstants";
import { useGoogleLogin } from "@react-oauth/google";
import { GLogin } from "../../service/OAuthLogin";
import MicrosoftIcon from "../../resources/icons/microsoft.svg";
import { microsoftAuth } from "../../service/OAuthLogin";
import GoogleIcon from "../../resources/icons/Google-Social-icon.svg";
import ReCAPTCHA from "react-google-recaptcha";
import { BiHide, BiShow } from "react-icons/bi";
import {
  schema1,
  schema2,
  state1,
  state2,
  ORG_SIGN_UP_FIELD_LIMITS,
  STATES_IN_INDIA,
} from "./SignUpOrgSideCurtains.utils";
import CircleGreenTickIcon from "../../resources/icons/circleGreenTick.svg";
import SignUpInModalLoader from "../../common/SignUpInModalLoader/SignUpInModalLoader";
import ErrorSignUpInNotification from "../../common/ErrorSignUpInNotification/ErrorSignUpInNotification";

const SignUpSideCurtains = ({ toggleComponent, closeModal }) => {
  const { t } = useTranslation();

  const phobe_country_code = require("../../resources/json/phobe_country_code.json");
  const authContext = useContext(AuthContext);
  const [message, setMessage] = useState("");
  const [isSignOrg, setIsSigOrg] = useState(false);
  const [signUpScheme, setSignUpSchema] = useState(schema3);
  const [hideShowpassword, setHideShowpassword] = useState("password");
  const [hideShowConfpassword, setHideShowConfpassword] = useState("password");
  const [captchaValue, setCaptchaValue] = useState(null);
  const [orgSignUpState, setOrgSignUpState] = useState(0);
  const [orgSignUpValues, setOrgSignUpValues] = useState({});
  const [isSignUp, setIsSignUp] = useState(false);
  const [basicSignUpMessage, setBasicSignUpMessage] = useState("");
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorDetails, setErrorDetails] = useState(null);
  const [isCaptchaRequired, setIsCaptchaRequired] = useState(false);
  const [isLoadingOrgDetails, setIsLoadingOrgDetails] = useState(true);
  const [orgFlowType, setOrgFlowType] = useState(null);
  const [authProviders, setAuthProviders] = useState({ signup: [] });
  const [domainError, setDomainError] = useState(null);

  const personalAccountRecaptchaRef = useRef(null);
  const existingOrgRecaptchaRef = useRef(null);
  const createOrgRecaptchaRef = useRef(null);

  const existingOrgInitialState = {
    ...state,
    captcha: "",
  };

  useEffect(() => {
    return () => {
      resetAllCaptchas();
    };
  }, []);

  useEffect(() => {
    resetAllCaptchas();
  }, [orgSignUpState, isSignOrg, orgFlowType]);

  const resetAllCaptchas = () => {
    if (personalAccountRecaptchaRef.current) {
      personalAccountRecaptchaRef.current.reset();
    }
    if (existingOrgRecaptchaRef.current) {
      existingOrgRecaptchaRef.current.reset();
    }
    if (createOrgRecaptchaRef.current) {
      createOrgRecaptchaRef.current.reset();
    }
    setCaptchaValue(null);
  };

  const resetCaptcha = (ref) => {
    if (ref?.current) {
      ref.current.reset();
      setCaptchaValue(null);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoadingOrgDetails(true);
        const response = await GET(EntityConstants.GET_ORGANISATION_DETAILS);
        const data = await response.json();

        if (response.ok) {
          setAuthProviders(data.auth_providers || { signup: [] });
          setIsCaptchaRequired(data.recaptcha_flag === true);
        } else {
          toast.error(t("homeAuthPages:failed_fetch_org_details"));
          setIsCaptchaRequired(true);
          setAuthProviders({ signup: [] });
        }
      } catch (error) {
        toast.error(t("homeAuthPages:something_went_wrong"));
        setIsCaptchaRequired(true);
        setAuthProviders({ signup: [] });
      } finally {
        setIsLoadingOrgDetails(false);
      }
    };

    fetchData();
  }, []);

  const handleError = (errorData) => {
    setErrorDetails(errorData);
    setShowErrorModal(true);
  };

  const handleCloseErrorModal = () => {
    setShowErrorModal(false);
    setErrorDetails(null);
  };

  const InputContainerHeight = "4.375rem";
  const InputBorderRadius = "0.5rem";

  const sanitizeNameInput = (value) =>
    value
      .replace(/[^\p{L}\p{M} .'-]/gu, "")
      .replace(/^[^\p{L}]+/u, "")
      .slice(0, SIGN_UP_FIELD_LIMITS.name);

  const sanitizePhoneInput = (value) =>
    value.replace(/\D/g, "").slice(0, SIGN_UP_FIELD_LIMITS.phone);

  const sanitizeOrganisationNameInput = (value) =>
    value
      .replace(/[^\p{L}\p{M}\p{N} .,&'()/-]/gu, "")
      .replace(/^[^\p{L}\p{N}]+/u, "")
      .slice(0, ORG_SIGN_UP_FIELD_LIMITS.orgName);

  const sanitizeGstinInput = (value) =>
    value
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, "")
      .slice(0, ORG_SIGN_UP_FIELD_LIMITS.gstin);

  const handleCaptchaChange = (value) => {
    setCaptchaValue(value);
  };

  const validateEmailDomain = async (email) => {
    if (!email || !email.includes("@")) {
      setDomainError(null);
      return true;
    }

    try {
      const response = await POST(EntityConstants.POST_EMAIL_DOMAIN, {
        email,
      });
      const data = await response.json();

      if (!data.success) {
        setDomainError(data.message);
        return false;
      }

      setDomainError(null);
      return true;
    } catch (error) {
      console.error("Domain validation error:", error);
      setDomainError(null);
      return true;
    }
  };

  const handleSubmit = async (values) => {
    try {
      setIsSignUp(true);
      if (isCaptchaRequired && !captchaValue) {
        toast.error(t("homeAuthPages:captcha_complete"));
        return;
      }
      const user = { user_type: "verifier" };
      values = {
        ...values,
        ...user,
        ...AppConstants.TENANT,
        ...AppConstants.ENTITY,
        ...(isCaptchaRequired && { captchaToken: captchaValue }),
      };
      const formData = new FormData();

      // Append all values to FormData
      for (const key in values) {
        if (values.hasOwnProperty(key)) {
          formData.append(key, values[key]);
        }
      }

      const response = await POSTFORMDATA(
        EntityConstants.SIGN_UP_VERIFIER,
        formData,
      );
      const data = await response.json();

      resetCaptcha(personalAccountRecaptchaRef);

      if (response.ok) {
        toast.success(t("homeAuthPages:user_created_success"));
        setMessage(data.message);
        navigator("/");
        return;
      }
    } catch (error) {
      resetCaptcha(personalAccountRecaptchaRef);

      const errorData = await error.response.json();
      if (errorData.error && errorData.message) {
        handleError(errorData);
      } else {
        toast.error(
          errorData?.error ||
            errorData?.message ||
            t("homeAuthPages:signup_failed"),
        );
      }
    } finally {
      setIsSignUp(false);
    }
  };

  const saveOrgSignUp1 = async (values) => {
    const isDomainValid = await validateEmailDomain(values.email);
    if (!isDomainValid) return;

    setOrgSignUpValues(values);
    resetCaptcha(createOrgRecaptchaRef);
    setOrgSignUpState(2);
  };

  const handleOrgSignUpSubmit = async (values) => {
    try {
      setIsSignUp(true);
      if (!captchaValue) {
        toast.error(t("homeAuthPages:captcha_complete"));
        return;
      }
      const finalValues = {
        ...values,
        ...orgSignUpValues,
        ...(isCaptchaRequired && { captchaToken: captchaValue }),
        org_type: "organisation",
      };

      // Create a FormData instance
      const formData = new FormData();

      // Append all fields to FormData
      Object.keys(finalValues).forEach((key) => {
        const value = finalValues[key];

        if (value === null || value === undefined || value === "") return;

        if (value instanceof File || value instanceof Blob) {
          formData.append(key, value);
          return;
        }

        if (Array.isArray(value)) {
          value.forEach((file) => {
            if (file instanceof File || file instanceof Blob) {
              formData.append(key, file);
            }
          });
          return;
        }

        formData.append(key, value);
      });

      const response = await POSTFORMDATA(
        EntityConstants.SIGN_UP_VERIFIER,
        formData,
      );
      const data = await response.json();

      resetCaptcha(createOrgRecaptchaRef);

      if (response.ok) {
        toast.success(t("homeAuthPages:user_created_success"));
        setMessage(data.message);
        setOrgSignUpState(3);
        setIsSignUp(false);
        return;
      } else if (response.status === 404) {
        toast.error(data?.message);
      } else {
        toast.error(data?.message);
      }
    } catch (error) {
      resetCaptcha(createOrgRecaptchaRef);

      // Handle unexpected errors
      const errorData = await error.response.json();
      if (errorData.error && errorData.message) {
        handleError(errorData); // Show error modal
      } else {
        toast.error(
          errorData?.error ||
            errorData?.message ||
            t("homeAuthPages:signup_failed"),
        );
      }
    } finally {
      setIsSignUp(false);
    }
  };

  const signup = useGoogleLogin({
    onSuccess: async (credentialResponse) => {
      const login = await GLogin(credentialResponse, "success");
      if (login === true) {
        authContext.login() && authContext.googlelogin();
        closeModal();
      }
    },
    onError: (error) => {
      GLogin(error, "failed");
    },
  });

  const handleToggleComponent = () => {
    resetAllCaptchas();
    toggleComponent();
  };

  const handleSetOrgFlow = (type, state) => {
    resetAllCaptchas();
    setIsSigOrg(true);
    setOrgFlowType(type);
    setOrgSignUpState(state);
  };

  if (isLoadingOrgDetails) {
    return (
      <div className="flex justify-center items-center h-64">
        <SignUpInModalLoader />
      </div>
    );
  }

  if (!basicSignUpMessage && !message && !isSignOrg) {
    return (
      <>
        <Formik
          enableReinitialize
          initialValues={state}
          validationSchema={signUpScheme}
          onSubmit={(values) => handleSubmit(values)}
        >
          {({
            values,
            touched,
            errors,
            handleChange,
            handleSubmit,
            setFieldValue,
          }) => (
            <Form
              noValidate
              onSubmit={handleSubmit}
              className="flex flex-col justify-center w-full"
            >
              <div className="flex flex-col justify-center w-full overflow-x-hidden overflow-y-auto px-8 py-4">
                {authProviders?.signup?.includes("credentials") && (
                  <>
                    <h3 className="text-4xl font-medium leading-6 mt-1 text-cfSignInSignUpHeadingText">
                      {t("homeAuthPages:sign_up")}
                    </h3>
                    <div className="py-3 w-full">
                      <p className="text-base text-left text-cfSignUpToYourOrganisationAccountText">
                        {t("homeAuthPages:signup_personal_account")}
                      </p>
                    </div>
                    <div
                      id="inputSignUp"
                      className={`flex flex-col gap-[0.625rem] w-full`}
                    >
                      <div
                        className={`flex flex-col gap-4 rounded-md bg-inherit w-full h-[${InputContainerHeight}]`}
                      >
                        <label
                          htmlFor="sign-up-individual-first-name-input"
                          className="my-auto font-medium text-sm text-cfSignInSignUpInputLabelText text-left after:content-['*'] after:text-red-500"
                        >
                          {t("homeAuthPages:first_name")}
                        </label>
                        <input
                          type="text"
                          name="first_name"
                          id="sign-up-individual-first-name-input"
                          placeholder={`${t("homeAuthPages:first_name")}`}
                          className="rounded-md w-full h-12 focus:outline-none border border-cfSignInSignUpInputBorder text-base text-cfSignInSignUpInputPlaceholderText"
                          style={{ borderRadius: InputBorderRadius }}
                          maxLength={SIGN_UP_FIELD_LIMITS.name}
                          value={values.first_name}
                          onChange={(e) =>
                            setFieldValue(
                              "first_name",
                              sanitizeNameInput(e.target.value),
                            )
                          }
                        />
                      </div>
                      <p
                        id="sign-up-individual-first-name-error-message"
                        className="text-cfSignInSignUpInputPlaceholderText text-left text-red-600"
                      >
                        {errors.first_name &&
                          touched.first_name &&
                          `${errors.first_name}`}
                      </p>
                      <div
                        className={`flex flex-col gap-4 rounded-md bg-inherit w-full h-[${InputContainerHeight}]`}
                      >
                        <label
                          htmlFor="sign-up-individual-last-name-input"
                          className="my-auto font-medium text-sm text-cfSignInSignUpInputLabelText text-left after:content-['*'] after:text-red-500"
                        >
                          {t("homeAuthPages:last_name")}
                        </label>
                        <input
                          type="text"
                          name="last_name"
                          id="sign-up-individual-last-name-input"
                          placeholder={`${t("homeAuthPages:last_name")}`}
                          className="rounded-md w-full h-12 focus:outline-none border border-cfSignInSignUpInputBorder text-base text-cfSignInSignUpInputPlaceholderText"
                          style={{ borderRadius: InputBorderRadius }}
                          maxLength={SIGN_UP_FIELD_LIMITS.name}
                          value={values.last_name}
                          onChange={(e) =>
                            setFieldValue(
                              "last_name",
                              sanitizeNameInput(e.target.value),
                            )
                          }
                        />
                      </div>
                      <p
                        id="sign-up-individual-last-name-error-message"
                        className="text-cfSignInSignUpInputPlaceholderText text-left text-red-600"
                      >
                        {errors.last_name &&
                          touched.last_name &&
                          `${errors.last_name}`}
                      </p>
                      <div
                        className={`flex flex-col gap-4 rounded-md bg-inherit w-full h-[${InputContainerHeight}]`}
                      >
                        <label
                          htmlFor="sign-up-individual-email-input"
                          className="my-auto font-medium text-sm text-cfSignInSignUpInputLabelText text-left after:content-['*'] after:text-red-500"
                        >
                          {t("homeAuthPages:email")}
                        </label>
                        <input
                          type="email"
                          name="email"
                          id="sign-up-individual-email-input"
                          placeholder={`${t("homeAuthPages:email")}`}
                          className="rounded-md w-full h-12 focus:outline-none border border-cfSignInSignUpInputBorder text-base text-cfSignInSignUpInputPlaceholderText"
                          style={{ borderRadius: InputBorderRadius }}
                          maxLength={SIGN_UP_FIELD_LIMITS.email}
                          value={values.email}
                          onChange={handleChange}
                        />
                      </div>
                      <p
                        id="sign-up-individual-email-error-message"
                        className="text-cfSignInSignUpInputPlaceholderText text-left text-red-600"
                      >
                        {errors.email && touched.email && `${errors.email}`}
                      </p>
                      <div
                        className={`flex flex-col gap-4 rounded-md bg-inherit w-full h-[${InputContainerHeight}]`}
                      >
                        <label
                          htmlFor="sign-up-individual-phone-input"
                          className="my-auto font-medium text-sm text-cfSignInSignUpInputLabelText text-left"
                        >
                          {t("homeAuthPages:phone")}
                        </label>
                        <div className="flex items-center justify-between rounded-md w-full h-11 mr-[.14rem] border border-cfSignInSignUpInputBorder">
                          <div className="flex items-center bg-cfSecondary rounded-l-lg h-10 w-4/12">
                            <select
                              type="country_code"
                              name="country_code"
                              id="sign-up-individual-country-code-select"
                              placeholder={t("homeAuthPages:country_code")}
                              className={`border-l-0 border-t-0 border-b-0 border-r border-cfSignInSignUpInputBorder rounded-l-lg cf-text-base h-10 mr-[.14rem] pl-2 focus:outline-none w-full`}
                              value={values.country_code}
                              onChange={(e) => {
                                handleChange(e);
                              }}
                            >
                              {phobe_country_code.map(
                                ({ phone, name, emoji }) => (
                                  <option value={phone}>
                                    {emoji}
                                    {phone}
                                  </option>
                                ),
                              )}
                            </select>
                          </div>
                          <input
                            type="text"
                            name="phone_no"
                            id="sign-up-individual-phone-input"
                            placeholder={t(
                              "homeAuthPages:phone_number_optional",
                            )}
                            className=" border-none w-full h-10 mr-[.14rem] pl-2 focus:outline-none"
                            style={{ borderRadius: "5px" }}
                            inputMode="numeric"
                            maxLength={SIGN_UP_FIELD_LIMITS.phone}
                            value={values.phone_no}
                            onChange={(e) =>
                              setFieldValue(
                                "phone_no",
                                sanitizePhoneInput(e.target.value),
                              )
                            }
                          />
                        </div>
                      </div>
                      <p
                        id="sign-up-individual-phone-error-message"
                        className="text-cfSignInSignUpInputPlaceholderText text-left text-red-600"
                      >
                        {errors.phone_no &&
                          touched.phone_no &&
                          `${t("homeAuthPages:enter_valid_phone")}`}
                      </p>
                      <div
                        className={`flex flex-col gap-4 rounded-md bg-inherit w-full h-[${InputContainerHeight}]`}
                      >
                        <label
                          htmlFor="sign-up-individual-password-input"
                          className="my-auto font-medium text-sm text-cfSignInSignUpInputLabelText text-left after:content-['*'] after:text-red-500"
                        >
                          {t("homeAuthPages:password")}
                        </label>
                        <div className="flex items-center justify-between rounded-md w-full h-11 mr-[.14rem] pl-2 border border-cfSignInSignUpInputBorder">
                          <input
                            type={hideShowpassword}
                            name="password"
                            id="sign-up-individual-password-input"
                            placeholder={`${t("homeAuthPages:password")}`}
                            className="focus:outline-none text-base text-cfSignInSignUpInputPlaceholderText border-none w-full"
                            style={{ borderRadius: InputBorderRadius }}
                            maxLength={SIGN_UP_FIELD_LIMITS.password}
                            value={values.password}
                            onChange={handleChange}
                          />
                          <div className="pl-1 pr-2 text-cfSignInSignUpInputPlaceholderText">
                            {hideShowpassword === "password" && (
                              <BiShow
                                id="sign-up-individual-password-show-toggle-button"
                                onClick={() => {
                                  setHideShowpassword("text");
                                  if (hideShowConfpassword === "text") {
                                    setHideShowConfpassword("password");
                                  }
                                }}
                                className="cursor-pointer"
                              />
                            )}
                            {hideShowpassword === "text" && (
                              <BiHide
                                id="sign-up-individual-password-hide-toggle-button"
                                onClick={() => {
                                  setHideShowpassword("password");
                                }}
                                className="cursor-pointer"
                              />
                            )}
                          </div>
                        </div>
                      </div>
                      <p
                        id="sign-up-individual-password-error-message"
                        className="text-cfSignInSignUpInputPlaceholderText text-left text-red-600"
                      >
                        {errors.password && touched.password && errors.password}
                      </p>
                      <div
                        className={`flex flex-col gap-4 rounded-md bg-inherit w-full h-[${InputContainerHeight}]`}
                      >
                        <label
                          htmlFor="sign-up-individual-confirm-password-input"
                          className="my-auto font-medium text-sm text-cfSignInSignUpInputLabelText text-left after:content-['*'] after:text-red-500"
                        >
                          {t("homeAuthPages:re_enter_password")}
                        </label>
                        <div className="flex items-center justify-between rounded-md w-full h-11 mr-[.14rem] pl-2 border border-cfSignInSignUpInputBorder">
                          <input
                            type={hideShowConfpassword}
                            name="confirm_password"
                            id="sign-up-individual-confirm-password-input"
                            placeholder={`${t(
                              "homeAuthPages:re_enter_password",
                            )}`}
                            className="focus:outline-none text-base text-cfSignInSignUpInputPlaceholderText border-none w-full"
                            style={{ borderRadius: InputBorderRadius }}
                            maxLength={SIGN_UP_FIELD_LIMITS.password}
                            value={values.confirm_password}
                            onChange={handleChange}
                          />
                          <div className="pl-1 pr-2 text-cfSignInSignUpInputPlaceholderText">
                            {hideShowConfpassword === "password" && (
                              <BiShow
                                id="sign-up-individual-confirm-password-show-toggle-button"
                                onClick={() => {
                                  setHideShowConfpassword("text");
                                  if (hideShowpassword === "text") {
                                    setHideShowpassword("password");
                                  }
                                }}
                                className="cursor-pointer"
                              />
                            )}
                            {hideShowConfpassword === "text" && (
                              <BiHide
                                id="sign-up-individual-confirm-password-hide-toggle-button"
                                onClick={() =>
                                  setHideShowConfpassword("password")
                                }
                                className="cursor-pointer"
                              />
                            )}
                          </div>
                        </div>
                      </div>
                      <p
                        id="sign-up-individual-confirm-password-error-message"
                        className="text-cfSignInSignUpInputPlaceholderText text-left text-red-600"
                      >
                        {errors.confirm_password &&
                          touched.confirm_password &&
                          `${errors.confirm_password}`}
                      </p>
                    </div>
                  </>
                )}

                <div className="w-full my-3 flex flex-col gap-[0.875rem]">
                  {authProviders?.signup?.includes("credentials") &&
                    isCaptchaRequired && (
                      <div className="flex custom-recaptcha-width justify-center items-center">
                        <ReCAPTCHA
                          ref={personalAccountRecaptchaRef}
                          sitekey={AppConstants.GOOGLE_CAPTCHA_SITE_KEY}
                          onChange={handleCaptchaChange}
                        />
                      </div>
                    )}
                  {authProviders?.signup?.includes("credentials") && (
                    <div>
                      <input
                        id="sign-up-individual-submit-button"
                        type="submit"
                        value={t("homeAuthPages:sign_up")}
                        disabled={
                          !values.first_name?.trim() ||
                          !values.last_name?.trim() ||
                          !values.email?.trim() ||
                          !values.password?.trim() ||
                          !values.confirm_password?.trim() ||
                          (isCaptchaRequired && !captchaValue)
                        }
                        className="rounded-xl text-base font-semibold bg-cfButton hover:bg-cfPrimaryDark py-1 px-5 text-cfSecondary w-full h-11 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-cfButton"
                      />
                    </div>
                  )}
                  {(authProviders?.signup?.includes("google") ||
                    authProviders?.signup?.includes("microsoft") ||
                    authProviders?.signup?.includes("new_organisation") ||
                    authProviders?.signup?.includes(
                      "existing_organisation",
                    )) && (
                    <>
                      {authProviders?.signup?.includes("credentials") &&
                        (authProviders?.signup?.includes("google") ||
                          authProviders?.signup?.includes("microsoft")) && (
                          <div className="flex items-center w-full gap-2.5 text-cfSignInSignUpOrText">
                            <hr className="w-1/2" />
                            <p>{t("homeAuthPages:or")}</p>
                            <hr className="w-1/2" />
                          </div>
                        )}
                      {!authProviders?.signup?.includes("credentials") &&
                        authProviders?.signup?.some((p) =>
                          [
                            "google",
                            "microsoft",
                            "new_organisation",
                            "existing_organisation",
                          ].includes(p),
                        ) && (
                          <h3 className="flex w-full items-center justify-center text-2xl font-semibold leading-6 text-cfSignInSignUpHeadingText px-8 text-center">
                            {t("homeAuthPages:sign_up")}
                          </h3>
                        )}
                      <div
                        className={`flex gap-4 ${
                          authProviders?.signup?.includes("google") &&
                          authProviders?.signup?.includes("microsoft")
                            ? "w-full justify-center"
                            : "w-full justify-center"
                        }`}
                      >
                        {authProviders?.signup?.includes("google") && (
                          <button
                            id="sign-up-google-oauth-button"
                            onClick={signup}
                            className="flex items-center gap-2 justify-center w-full h-11 border rounded-full focus:outline-none"
                          >
                            <img src={GoogleIcon} alt="google" />
                            <p className="font-semibold text-base font-CfPrimaryFont">
                              {t("homeAuthPages:sign_up")}
                            </p>
                          </button>
                        )}
                        {authProviders?.signup?.includes("microsoft") && (
                          <button
                            id="sign-up-microsoft-oauth-button"
                            type="button"
                            onClick={microsoftAuth}
                            className="flex items-center gap-2 justify-center w-full h-11 border rounded-full focus:outline-none"
                          >
                            <img src={MicrosoftIcon} alt="microsoft" />
                            <p className="font-semibold text-base font-CfPrimaryFont ">
                              {t("homeAuthPages:sign_up")}
                            </p>
                          </button>
                        )}
                      </div>
                    </>
                  )}
                </div>

                <div className="flex text-[1rem] leading-[1.2rem] px-1 pb-1 text-s mt-2 justify-center">
                  <p className="text-cfDontHaveAccountText">
                    {t("homeAuthPages:already_have_account")}
                  </p>
                  &nbsp;
                  <button
                    id="sign-up-sign-in-navigation-button"
                    onClick={toggleComponent}
                    className="flex focus:outline-none"
                  >
                    <p className="hover:text-cfPrimaryDark font-semibold text-cfButton">
                      {t("homeAuthPages:sign_in")}
                    </p>
                  </button>
                </div>
              </div>

              {(authProviders?.signup?.includes("new_organisation") ||
                authProviders?.signup?.includes("existing_organisation")) && (
                <div className="bg-cfSignUpBottomSection h-fit w-full flex flex-col items-center gap-3 px-8 py-6 rounded-b-[1.25rem]">
                  {authProviders?.signup?.includes("new_organisation") && (
                    <>
                      <p className="text-cfSignUpAreYouAnAdministratorText text-sm">
                        {t("homeAuthPages:are_you_admin")}
                      </p>
                      <button
                        id="sign-up-create-org-flow-button"
                        onClick={() => handleSetOrgFlow("new", 1)}
                        className="flex items-center gap-2 justify-center w-full px-2 h-11 border rounded-xl bg-cfSecondary focus:outline-none"
                      >
                        {t("homeAuthPages:create_your_organisation")}
                      </button>
                    </>
                  )}
                  {authProviders?.signup?.includes("new_organisation") &&
                    authProviders?.signup?.includes(
                      "existing_organisation",
                    ) && (
                      <div className="flex items-center w-full gap-2.5 text-cfSignInSignUpOrText">
                        <hr className="w-1/2" />
                        <p>{t("homeAuthPages:or")}</p>
                        <hr className="w-1/2" />
                      </div>
                    )}
                  {authProviders?.signup?.includes("existing_organisation") && (
                    <button
                      id="sign-up-existing-org-flow-button"
                      onClick={() => handleSetOrgFlow("existing", 1)}
                      className="flex items-center gap-2 justify-center w-full px-2 h-11 border rounded-xl bg-cfSecondary focus:outline-none"
                    >
                      {t("homeAuthPages:continue_existing_org")}
                    </button>
                  )}
                </div>
              )}
            </Form>
          )}
        </Formik>
        {isSignUp && <SignUpInModalLoader />}
        {showErrorModal && (
          <ErrorSignUpInNotification
            closeModal={handleCloseErrorModal}
            error={errorDetails?.error}
            message={errorDetails?.message}
          />
        )}
      </>
    );
  } else if (
    !basicSignUpMessage &&
    !message &&
    isSignOrg &&
    orgFlowType === "existing" &&
    orgSignUpState === 1
  ) {
    return (
      <>
        <Formik
          initialValues={existingOrgInitialState}
          validationSchema={schema}
          onSubmit={async (values, { setSubmitting }) => {
            const payload = {
              first_name: values.first_name,
              last_name: values.last_name,
              email: values.email,
              password: values.password,
              confirm_password: values.confirm_password,
              phone_no: values.phone_no || "",
              org_type: "organisation",
            };

            const formData = new FormData();
            Object.entries(payload).forEach(([key, value]) => {
              if (value !== undefined && value !== null) {
                formData.append(key, value);
              }
            });

            try {
              if (!captchaValue) {
                toast.error(t("homeAuthPages:captcha_complete"));
                setSubmitting(false);
                return;
              }

              setIsSignUp(true);

              const response = await POSTFORMDATA(
                EntityConstants.SIGN_UP_VERIFIER,
                formData,
              );

              const data = await response.json();

              resetCaptcha(existingOrgRecaptchaRef);

              if (response.ok) {
                setMessage(data.message);
                setOrgSignUpState(3);
              } else {
                const errorMessage =
                  data?.message || t("homeAuthPages:signup_failed");
                toast.error(errorMessage);
              }
            } catch (error) {
              resetCaptcha(existingOrgRecaptchaRef);

              if (error.response) {
                try {
                  const errorData = await error.response.json();
                  const errorMessage =
                    errorData?.message ||
                    errorData?.error ||
                    t("homeAuthPages:signup_failed");
                  toast.error(errorMessage);
                } catch {
                  toast.error(t("homeAuthPages:failed_parse_error"));
                }
              } else if (error.message === "HTTP error") {
                toast.error(t("homeAuthPages:server_error_retry"));
              } else {
                toast.error(t("homeAuthPages:unexpected_error"));
              }
            } finally {
              setIsSignUp(false);
              setSubmitting(false);
            }
          }}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleSubmit,
            setFieldValue,
          }) => (
            <Form
              noValidate
              onSubmit={handleSubmit}
              className="flex flex-col justify-center w-full max-w-[28rem] px-8 py-4"
            >
              <h3 className="text-4xl font-medium text-cfSignInSignUpHeadingText pb-6">
                {t("homeAuthPages:create_account_existing_org")}
              </h3>

              {/* <div className="flex flex-col px-8 py-6 gap-[0.625rem]"></div> */}
              {/* ${t("homeAuthPages:first_name")} */}
              <div
                id="inputSignUp"
                className={`flex flex-col gap-[0.625rem] w-full`}
              >
                <div
                  className={`flex flex-col gap-4 rounded-md bg-inherit w-full h-[${InputContainerHeight}]`}
                >
                  <label
                    htmlFor="sign-up-org-existing-first-name-input"
                    className="my-auto font-medium text-sm text-cfSignInSignUpInputLabelText text-left after:content-['*'] after:text-red-500"
                  >
                    {t("homeAuthPages:first_name")}
                  </label>
                  <input
                    type="text"
                    name="first_name"
                    id="sign-up-org-existing-first-name-input"
                    placeholder={`${t("homeAuthPages:first_name")}`}
                    className="rounded-md w-full h-12 focus:outline-none border border-cfSignInSignUpInputBorder text-base text-cfSignInSignUpInputPlaceholderText"
                    style={{ borderRadius: InputBorderRadius }}
                    maxLength={SIGN_UP_FIELD_LIMITS.name}
                    value={values.first_name}
                    onChange={(e) =>
                      setFieldValue(
                        "first_name",
                        sanitizeNameInput(e.target.value),
                      )
                    }
                  />
                </div>
                <p
                  id="sign-up-org-existing-first-name-error-message"
                  className="text-cfSignInSignUpInputPlaceholderText text-left text-red-600"
                >
                  {errors.first_name &&
                    touched.first_name &&
                    `${errors.first_name}`}
                </p>

                {/* ${t("homeAuthPages:last_name")} */}
                <div
                  className={`flex flex-col gap-4 rounded-md bg-inherit w-full h-[${InputContainerHeight}]`}
                >
                  <label
                    htmlFor="sign-up-org-existing-last-name-input"
                    className="my-auto font-medium text-sm text-cfSignInSignUpInputLabelText text-left after:content-['*'] after:text-red-500"
                  >
                    {t("homeAuthPages:last_name")}
                  </label>
                  <input
                    type="text"
                    name="last_name"
                    id="sign-up-org-existing-last-name-input"
                    placeholder={`${t("homeAuthPages:last_name")}`}
                    className="rounded-md w-full h-12 focus:outline-none border border-cfSignInSignUpInputBorder text-base text-cfSignInSignUpInputPlaceholderText"
                    style={{ borderRadius: InputBorderRadius }}
                    maxLength={SIGN_UP_FIELD_LIMITS.name}
                    value={values.last_name}
                    onChange={(e) =>
                      setFieldValue(
                        "last_name",
                        sanitizeNameInput(e.target.value),
                      )
                    }
                  />
                </div>
                <p
                  id="sign-up-org-existing-last-name-error-message"
                  className="text-cfSignInSignUpInputPlaceholderText text-left text-red-600"
                >
                  {errors.last_name &&
                    touched.last_name &&
                    `${errors.last_name}`}
                </p>

                <div
                  className={`flex flex-col gap-4 rounded-md bg-inherit w-full h-[${InputContainerHeight}]`}
                >
                  <label
                    htmlFor="sign-up-org-existing-email-input"
                    className="my-auto font-medium text-sm text-cfSignInSignUpInputLabelText text-left after:content-['*'] after:text-red-500"
                  >
                    {t("homeAuthPages:org_email")}
                  </label>
                  <input
                    type="email"
                    name="email"
                    id="sign-up-org-existing-email-input"
                    placeholder={t("homeAuthPages:email")}
                    className="rounded-md w-full h-12 focus:outline-none border border-cfSignInSignUpInputBorder text-base text-cfSignInSignUpInputPlaceholderText"
                    style={{ borderRadius: InputBorderRadius }}
                    maxLength={SIGN_UP_FIELD_LIMITS.email}
                    value={values.email}
                    onChange={handleChange}
                  />
                </div>
                <p
                  id="sign-up-org-existing-email-error-message"
                  className="text-cfSignInSignUpInputPlaceholderText text-left text-red-600"
                >
                  {errors.email && touched.email && `${errors.email}`}
                </p>

                {/* Phone */}
                <div
                  className={`flex flex-col gap-4 rounded-md bg-inherit w-full h-[${InputContainerHeight}]`}
                >
                  <label
                    htmlFor="sign-up-org-existing-phone-input"
                    className="my-auto font-medium text-sm text-cfSignInSignUpInputLabelText text-left"
                  >
                    {t("homeAuthPages:phone")}
                  </label>
                  <div className="flex items-center justify-between rounded-md w-full h-11 mr-[.14rem] border border-cfSignInSignUpInputBorder">
                    <div className="flex items-center bg-cfSecondary rounded-l-lg h-10 w-4/12">
                      <select
                        type="country_code"
                        name="country_code"
                        id="sign-up-org-existing-country-code-select"
                        placeholder={t("homeAuthPages:country_code")}
                        className={`border-l-0 border-t-0 border-b-0 border-r border-cfSignInSignUpInputBorder rounded-l-lg cf-text-base h-10 mr-[.14rem] pl-2 focus:outline-none w-full`}
                        value={values.country_code}
                        onChange={(e) => {
                          handleChange(e);
                        }}
                      >
                        {phobe_country_code.map(({ phone, name, emoji }) => (
                          <option value={phone}>
                            {emoji}
                            {phone}
                          </option>
                        ))}
                      </select>
                    </div>
                    <input
                      type="text"
                      name="phone_no"
                      id="sign-up-org-existing-phone-input"
                      placeholder={t("homeAuthPages:phone_number_optional")}
                      className=" border-none w-full h-10 mr-[.14rem] pl-2 focus:outline-none"
                      style={{ borderRadius: "5px" }}
                      inputMode="numeric"
                      maxLength={SIGN_UP_FIELD_LIMITS.phone}
                      value={values.phone_no}
                      onChange={(e) =>
                        setFieldValue(
                          "phone_no",
                          sanitizePhoneInput(e.target.value),
                        )
                      }
                    />
                  </div>
                </div>
                <p
                  id="sign-up-org-existing-phone-error-message"
                  className="text-cfSignInSignUpInputPlaceholderText text-left text-red-600"
                >
                  {errors.phone_no &&
                    touched.phone_no &&
                    `${t("homeAuthPages:enter_valid_phone")}`}
                </p>

                {/* ${t("homeAuthPages:password")} */}
                <div
                  className={`flex flex-col gap-4 rounded-md bg-inherit w-full h-[${InputContainerHeight}]`}
                >
                  <label
                    htmlFor="sign-up-org-existing-password-input"
                    className="my-auto font-medium text-sm text-cfSignInSignUpInputLabelText text-left after:content-['*'] after:text-red-500"
                  >
                    {t("homeAuthPages:password")}
                  </label>
                  <div className="flex items-center justify-between rounded-md w-full h-11 mr-[.14rem] pl-2 border border-cfSignInSignUpInputBorder">
                    <input
                      type={hideShowpassword}
                      name="password"
                      id="sign-up-org-existing-password-input"
                      placeholder={`${t("homeAuthPages:password")}`}
                      className="focus:outline-none text-base text-cfSignInSignUpInputPlaceholderText border-none w-full"
                      style={{ borderRadius: InputBorderRadius }}
                      maxLength={SIGN_UP_FIELD_LIMITS.password}
                      value={values.password}
                      onChange={handleChange}
                    />
                    <div className="pl-1 pr-2 text-cfSignInSignUpInputPlaceholderText">
                      {hideShowpassword === "password" && (
                        <BiShow
                          id="sign-up-org-existing-password-show-toggle-button"
                          onClick={() => {
                            setHideShowpassword("text");
                            if (hideShowConfpassword === "text") {
                              setHideShowConfpassword("password");
                            }
                          }}
                          className="cursor-pointer"
                        />
                      )}
                      {hideShowpassword === "text" && (
                        <BiHide
                          id="sign-up-org-existing-password-hide-toggle-button"
                          onClick={() => {
                            setHideShowpassword("password");
                          }}
                          className="cursor-pointer"
                        />
                      )}
                    </div>
                  </div>
                </div>
                <p
                  id="sign-up-org-existing-password-error-message"
                  className="text-cfSignInSignUpInputPlaceholderText text-left text-red-600"
                >
                  {errors.password && touched.password && errors.password}
                </p>

                {/* Confirm password */}
                <div
                  className={`flex flex-col gap-4 rounded-md bg-inherit w-full h-[${InputContainerHeight}]`}
                >
                  <label
                    htmlFor="sign-up-org-existing-confirm-password-input"
                    className="my-auto font-medium text-sm text-cfSignInSignUpInputLabelText text-left after:content-['*'] after:text-red-500"
                  >
                    {t("homeAuthPages:re_enter_password")}
                  </label>
                  <div className="flex items-center justify-between rounded-md w-full h-11 mr-[.14rem] pl-2 border border-cfSignInSignUpInputBorder">
                    <input
                      type={hideShowConfpassword}
                      name="confirm_password"
                      id="sign-up-org-existing-confirm-password-input"
                      placeholder={`${t("homeAuthPages:re_enter_password")}`}
                      className="focus:outline-none text-base text-cfSignInSignUpInputPlaceholderText border-none w-full"
                      style={{ borderRadius: InputBorderRadius }}
                      maxLength={SIGN_UP_FIELD_LIMITS.password}
                      value={values.confirm_password}
                      onChange={handleChange}
                    />
                    <div className="pl-1 pr-2 text-cfSignInSignUpInputPlaceholderText">
                      {hideShowConfpassword === "password" && (
                        <BiShow
                          id="sign-up-org-existing-confirm-password-show-toggle-button"
                          onClick={() => {
                            setHideShowConfpassword("text");
                            if (hideShowpassword === "text") {
                              setHideShowpassword("password");
                            }
                          }}
                          className="cursor-pointer"
                        />
                      )}
                      {hideShowConfpassword === "text" && (
                        <BiHide
                          id="sign-up-org-existing-confirm-password-hide-toggle-button"
                          onClick={() => setHideShowConfpassword("password")}
                          className="cursor-pointer"
                        />
                      )}
                    </div>
                  </div>
                </div>
                <p
                  id="sign-up-org-existing-confirm-password-error-message"
                  className="text-cfSignInSignUpInputPlaceholderText text-left text-red-600"
                >
                  {errors.confirm_password &&
                    touched.confirm_password &&
                    `${errors.confirm_password}`}
                </p>

                {/* CAPTCHA */}
                <div className="flex justify-center mt-2">
                  <ReCAPTCHA
                    ref={existingOrgRecaptchaRef}
                    sitekey={AppConstants.GOOGLE_CAPTCHA_SITE_KEY}
                    onChange={(val) => setCaptchaValue(val)}
                  />
                </div>

                {/* Submit */}
                <button
                  id="sign-up-org-existing-submit-button"
                  type="submit"
                  disabled={
                    isSignUp ||
                    !values.first_name?.trim() ||
                    !values.last_name?.trim() ||
                    !values.email?.trim() ||
                    !values.password?.trim() ||
                    !values.confirm_password?.trim() ||
                    !captchaValue
                  }
                  className="mt-2 h-11 bg-cfButton text-white rounded-xl disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center focus:outline-none hover:bg-cfPrimaryDark disabled:hover:bg-cfButton"
                >
                  {t("homeAuthPages:continue")}
                </button>
              </div>
              <div className="flex text-[1rem] leading-[1.2rem] px-1 pb-1 text-s mt-4 justify-center">
                <p className="text-cfDontHaveAccountText">
                  {t("homeAuthPages:already_have_account")}
                </p>
                &nbsp;
                <button
                  id="sign-up-org-existing-sign-in-navigation-button"
                  onClick={handleToggleComponent}
                  className="flex focus:outline-none"
                >
                  <p className="hover:text-cfPrimaryDark font-semibold text-cfButton">
                    {t("homeAuthPages:sign_in")}
                  </p>
                </button>
              </div>
              <div className="flex text-[1rem] leading-[1.2rem] px-1 pb-1 text-s mt-2 justify-center">
                <p className="text-cfDontHaveAccountText">
                  {t("homeAuthPages:dont_have_org")}
                </p>
                &nbsp;
                <button
                  id="sign-up-org-existing-create-org-navigation-button"
                  type="button"
                  onClick={() => handleSetOrgFlow("new", 1)}
                  className="flex focus:outline-none"
                >
                  <p className="hover:text-cfPrimaryDark font-semibold text-cfButton">
                    {t("homeAuthPages:create_organisation")}
                  </p>
                </button>
              </div>
            </Form>
          )}
        </Formik>

        {isSignUp && <SignUpInModalLoader />}

        {showErrorModal && (
          <ErrorSignUpInNotification
            closeModal={handleCloseErrorModal}
            error={errorDetails?.error}
            message={errorDetails?.message}
          />
        )}
      </>
    );
  } else if (
    !basicSignUpMessage &&
    !message &&
    isSignOrg &&
    orgSignUpState === 1
  ) {
    /*Orginasation Registration Flow 1*/
    return (
      <>
        <Formik
          enableReinitialize
          initialValues={state1}
          validationSchema={schema1}
          context={{ setDomainError }}
          onSubmit={(values) => saveOrgSignUp1(values)}
        >
          {({
            values,
            touched,
            errors,
            handleChange,
            handleSubmit,
            setFieldValue,
            setFieldTouched,
          }) => (
            <Form
              noValidate
              onSubmit={handleSubmit}
              className="flex flex-col justify-center w-full min-w-[25rem] max-w-[28rem] py-4"
            >
              <h3 className="text-4xl font-medium leading-9 text-cfSignInSignUpHeadingText px-8 h-f">
                {t("homeAuthPages:create_account_your_org")}
              </h3>
              <div className="flex flex-col justify-center items-center text-center w-full overflow-x-hidden overflow-y-auto px-8 pt-2 pb-6">
                <div className="py-3 w-full">
                  {/* DOCUMENT MESSSAGE */}

                  {/* <p className="text-base text-left text-cfSignUpToYourOrganisationAccountText">
                    Only admins with relevant documents can create an
                    organisation.
                  </p> */}
                </div>
                <div
                  id="inputSignUp"
                  className={`flex flex-col gap-[0.625rem] w-full`}
                >
                  <div
                    className={`flex flex-col gap-4 rounded-md bg-inherit w-full h-[${InputContainerHeight}]`}
                  >
                    <label
                      htmlFor="sign-up-org-step1-first-name-input"
                      className="my-auto font-medium text-sm text-cfSignInSignUpInputLabelText text-left after:content-['*'] after:text-red-500 "
                    >
                      {t("homeAuthPages:first_name")}
                    </label>
                    <input
                      type="text"
                      name="first_name"
                      id="sign-up-org-step1-first-name-input"
                      placeholder={`${t("homeAuthPages:first_name")}`}
                      className="rounded-md w-full h-12 focus:outline-none border border-cfSignInSignUpInputBorder text-base text-cfSignInSignUpInputPlaceholderText"
                      style={{ borderRadius: InputBorderRadius }}
                      maxLength={SIGN_UP_FIELD_LIMITS.name}
                      value={values.first_name}
                      onChange={(e) =>
                        setFieldValue(
                          "first_name",
                          sanitizeNameInput(e.target.value),
                        )
                      }
                    />
                  </div>
                  <p
                    id="sign-up-org-step1-first-name-error-message"
                    className="text-cfSignInSignUpInputPlaceholderText text-left text-red-600"
                  >
                    {errors.first_name &&
                      touched.first_name &&
                      `${errors.first_name}`}
                  </p>
                  <div
                    className={`flex flex-col gap-4 rounded-md bg-inherit w-full h-[${InputContainerHeight}]`}
                  >
                    <label
                      htmlFor="sign-up-org-step1-last-name-input"
                      className="my-auto font-medium text-sm text-cfSignInSignUpInputLabelText text-left after:content-['*'] after:text-red-500"
                    >
                      {t("homeAuthPages:last_name")}
                    </label>
                    <input
                      type="text"
                      name="last_name"
                      id="sign-up-org-step1-last-name-input"
                      placeholder={`${t("homeAuthPages:last_name")}`}
                      className="rounded-md w-full h-12 focus:outline-none border border-cfSignInSignUpInputBorder text-base text-cfSignInSignUpInputPlaceholderText"
                      style={{ borderRadius: InputBorderRadius }}
                      maxLength={SIGN_UP_FIELD_LIMITS.name}
                      value={values.last_name}
                      onChange={(e) =>
                        setFieldValue(
                          "last_name",
                          sanitizeNameInput(e.target.value),
                        )
                      }
                    />
                  </div>
                  <p
                    id="sign-up-org-step1-last-name-error-message"
                    className="text-cfSignInSignUpInputPlaceholderText text-left text-red-600"
                  >
                    {errors.last_name &&
                      touched.last_name &&
                      `${errors.last_name}`}
                  </p>
                  <div
                    className={`flex flex-col gap-4 rounded-md bg-inherit w-full h-[${InputContainerHeight}]`}
                  >
                    <label
                      htmlFor="sign-up-org-step1-email-input"
                      className="my-auto font-medium text-sm text-cfSignInSignUpInputLabelText text-left after:content-['*'] after:text-red-500 "
                    >
                      {t("homeAuthPages:org_email")}
                    </label>
                    <input
                      type="email"
                      name="email"
                      id="sign-up-org-step1-email-input"
                      placeholder={`${t("homeAuthPages:email")}`}
                      className="rounded-md w-full h-12 focus:outline-none border border-cfSignInSignUpInputBorder text-base text-cfSignInSignUpInputPlaceholderText"
                      style={{ borderRadius: InputBorderRadius }}
                      maxLength={SIGN_UP_FIELD_LIMITS.email}
                      value={values.email}
                      onChange={handleChange}
                      onBlur={async (e) => {
                        const email = e.target.value;
                        const isDomainValid = await validateEmailDomain(email);
                        if (!isDomainValid) {
                          setFieldValue("email", email, false);
                          setFieldTouched("email", true, false);
                        }
                      }}
                    />
                    {domainError && (
                      <p
                        id="sign-up-org-step1-email-domain-error-message"
                        className="text-cfSignInSignUpInputPlaceholderText text-left text-red-600"
                      >
                        {domainError}
                      </p>
                    )}
                  </div>
                  <p
                    id="sign-up-org-step1-email-error-message"
                    className="text-cfSignInSignUpInputPlaceholderText text-left text-red-600"
                  >
                    {errors.email && touched.email && `${errors.email}`}
                  </p>
                  <div
                    className={`flex flex-col gap-4 rounded-md bg-inherit w-full h-[${InputContainerHeight}]`}
                  >
                    <label
                      htmlFor="sign-up-org-step1-phone-input"
                      className="my-auto font-medium text-sm text-cfSignInSignUpInputLabelText text-left"
                    >
                      {t("homeAuthPages:phone")}
                    </label>
                    <div className="flex items-center justify-between rounded-md w-full h-11 mr-[.14rem] border border-cfSignInSignUpInputBorder">
                      <div className="flex items-center bg-cfSecondary rounded-l-lg h-10 w-4/12">
                        <select
                          type="country_code"
                          name="country_code"
                          id="sign-up-org-step1-country-code-select"
                          placeholder={t("homeAuthPages:country_code")}
                          className={`border-l-0 border-t-0 border-b-0 border-r border-cfSignInSignUpInputBorder rounded-l-lg cf-text-base h-10 mr-[.14rem] pl-2 focus:outline-none w-full`}
                          value={values.country_code}
                          onChange={(e) => {
                            handleChange(e);
                          }}
                        >
                          {phobe_country_code.map(({ phone, name, emoji }) => (
                            <option value={phone}>
                              {emoji}
                              {phone}
                            </option>
                          ))}
                        </select>
                      </div>
                      <input
                        type="text"
                        name="phone_no"
                        id="sign-up-org-step1-phone-input"
                        placeholder={t("homeAuthPages:phone_number_optional")}
                        className=" border-none w-full h-10 mr-[.14rem] pl-2 focus:outline-none"
                        style={{ borderRadius: "5px" }}
                        inputMode="numeric"
                        maxLength={SIGN_UP_FIELD_LIMITS.phone}
                        value={values.phone_no}
                        onChange={(e) =>
                          setFieldValue(
                            "phone_no",
                            sanitizePhoneInput(e.target.value),
                          )
                        }
                      />
                    </div>
                  </div>

                  <p
                    id="sign-up-org-step1-phone-error-message"
                    className="text-cfSignInSignUpInputPlaceholderText text-left text-red-600"
                  >
                    {errors.phone_no &&
                      touched.phone_no &&
                      `${t("homeAuthPages:enter_valid_phone")}`}
                  </p>
                  <div
                    className={`flex flex-col gap-4 rounded-md bg-inherit w-full h-[${InputContainerHeight}]`}
                  >
                    <label
                      htmlFor="sign-up-org-step1-password-input"
                      className="my-auto font-medium text-sm text-cfSignInSignUpInputLabelText text-left after:content-['*'] after:text-red-500"
                    >
                      {t("homeAuthPages:password")}
                    </label>
                    <div className="flex items-center justify-between rounded-md w-full h-11 mr-[.14rem] pl-2 border border-cfSignInSignUpInputBorder">
                      <input
                        type={hideShowpassword}
                        name="password"
                        id="sign-up-org-step1-password-input"
                        placeholder={`${t("homeAuthPages:password")}`}
                        className="focus:outline-none text-base text-cfSignInSignUpInputPlaceholderText border-none w-full"
                        style={{ borderRadius: InputBorderRadius }}
                        maxLength={SIGN_UP_FIELD_LIMITS.password}
                        value={values.password}
                        onChange={handleChange}
                      />
                      <div className="pl-1 pr-2 text-cfSignInSignUpInputPlaceholderText">
                        {hideShowpassword === "password" && (
                          <BiShow
                            id="sign-up-org-step1-password-show-toggle-button"
                            onClick={() => {
                              setHideShowpassword("text");
                              if (hideShowConfpassword === "text") {
                                setHideShowConfpassword("password");
                              }
                            }}
                            className="cursor-pointer"
                          />
                        )}
                        {hideShowpassword === "text" && (
                          <BiHide
                            id="sign-up-org-step1-password-hide-toggle-button"
                            onClick={() => {
                              setHideShowpassword("password");
                            }}
                            className="cursor-pointer"
                          />
                        )}
                      </div>
                    </div>
                  </div>
                  <p
                    id="sign-up-org-step1-password-error-message"
                    className="text-cfSignInSignUpInputPlaceholderText text-left text-red-600"
                  >
                    {errors.password && touched.password && errors.password}
                  </p>
                  <div
                    className={`flex flex-col gap-4 rounded-md bg-inherit w-full h-[${InputContainerHeight}]`}
                  >
                    <label
                      htmlFor="sign-up-org-step1-confirm-password-input"
                      className="my-auto font-medium text-sm text-cfSignInSignUpInputLabelText text-left after:content-['*'] after:text-red-500"
                    >
                      {t("homeAuthPages:re_enter_password")}
                    </label>
                    <div className="flex items-center justify-between rounded-md w-full h-11 mr-[.14rem] pl-2 border border-cfSignInSignUpInputBorder">
                      <input
                        type={hideShowConfpassword}
                        name="confirm_password"
                        id="sign-up-org-step1-confirm-password-input"
                        placeholder={`${t("homeAuthPages:re_enter_password")}`}
                        className="focus:outline-none text-base text-cfSignInSignUpInputPlaceholderText border-none w-full"
                        style={{ borderRadius: InputBorderRadius }}
                        maxLength={SIGN_UP_FIELD_LIMITS.password}
                        value={values.confirm_password}
                        onChange={handleChange}
                      />
                      <div className="pl-1 pr-2 text-cfSignInSignUpInputPlaceholderText">
                        {hideShowConfpassword === "password" && (
                          <BiShow
                            id="sign-up-org-step1-confirm-password-show-toggle-button"
                            onClick={() => {
                              setHideShowConfpassword("text");
                              if (hideShowpassword === "text") {
                                setHideShowpassword("password");
                              }
                            }}
                            className="cursor-pointer"
                          />
                        )}
                        {hideShowConfpassword === "text" && (
                          <BiHide
                            id="sign-up-org-step1-confirm-password-hide-toggle-button"
                            onClick={() => setHideShowConfpassword("password")}
                            className="cursor-pointer"
                          />
                        )}
                      </div>
                    </div>
                  </div>
                  <p
                    id="sign-up-org-step1-confirm-password-error-message"
                    className="text-cfSignInSignUpInputPlaceholderText text-left text-red-600"
                  >
                    {errors.confirm_password &&
                      touched.confirm_password &&
                      `${errors.confirm_password}`}
                  </p>
                </div>
                <div className="w-full my-3 flex flex-col gap-[0.875rem]">
                  <div>
                    <input
                      id="sign-up-org-step1-next-button"
                      type="submit"
                      value={t("homeAuthPages:next")}
                      disabled={
                        Boolean(domainError) ||
                        !values.first_name?.trim() ||
                        !values.last_name?.trim() ||
                        !values.email?.trim() ||
                        !values.password?.trim() ||
                        !values.confirm_password?.trim()
                      }
                      className="rounded-xl text-base font-semibold bg-cfButton hover:bg-cfPrimaryDark py-1 px-5 text-cfSecondary w-full h-11 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-cfButton"
                    />
                  </div>
                </div>

                <div className="flex flex-col items-center gap-2 text-[1rem] leading-[1.2rem]">
                  <div className="flex flex-col items-center gap-2 text-[1rem] leading-[1.3rem] text-center">
                    <div className="flex items-center justify-center gap-1 flex-wrap">
                      <span className="text-cfDontHaveAccountText">
                        {t("homeAuthPages:already_have_account")}
                      </span>
                      <button
                        id="sign-up-org-step1-sign-in-navigation-button"
                        onClick={handleToggleComponent}
                        className="focus:outline-none"
                      >
                        <span className="hover:text-cfPrimaryDark font-semibold text-cfButton">
                          {t("homeAuthPages:sign_in")}
                        </span>
                      </button>
                    </div>

                    <div className="flex items-center justify-center gap-1 flex-wrap">
                      <span className="text-cfDontHaveAccountText">
                        {t("homeAuthPages:already_have_org")}
                      </span>
                      <button
                        id="sign-up-org-step1-existing-org-navigation-button"
                        type="button"
                        onClick={() => handleSetOrgFlow("existing", 1)}
                        className="focus:outline-none"
                      >
                        <span className="hover:text-cfPrimaryDark font-semibold text-cfButton">
                          {t("homeAuthPages:continue")}
                        </span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </Form>
          )}
        </Formik>
        {showErrorModal && (
          <ErrorSignUpInNotification
            closeModal={handleCloseErrorModal}
            error={errorDetails?.error}
            message={errorDetails?.message}
          />
        )}
      </>
    );
  } else if (
    !basicSignUpMessage &&
    !message &&
    isSignOrg &&
    orgSignUpState === 2
  ) {
    /*Orginasation Registration Flow 2*/
    return (
      <>
        <Formik
          enableReinitialize
          initialValues={state2}
          validationSchema={schema2}
          onSubmit={(values) => handleOrgSignUpSubmit(values)}
        >
          {({
            values,
            touched,
            errors,
            handleChange,
            handleSubmit,
            setFieldValue,
            validateField,
          }) => (
            <Form
              noValidate
              onSubmit={handleSubmit}
              className="flex flex-col justify-center w-full min-w-[25rem] max-w-[28rem] py-4"
            >
              <h3 className="text-4xl font-medium leading-9 text-cfSignInSignUpHeadingText pl-8 pr-14 h-f">
                {t("homeAuthPages:create_account_your_org")}
              </h3>
              <div className="flex flex-col justify-center items-center text-center w-full overflow-x-hidden overflow-y-auto px-8 pt-2 pb-6">
                <div className="py-3 w-full">
                  {/* <p className="text-base text-left text-cfSignUpToYourOrganisationAccountText">
                    Only admins with relevant documents can create an
                    organisation.
                  </p> */}
                </div>
                <div
                  id="inputSignUp"
                  className="flex flex-col gap-[0.625rem] w-full"
                >
                  {/* Organisation Name Input */}
                  <div className="flex flex-col gap-4 rounded-md bg-inherit w-full h-[${InputContainerHeight}]">
                    <label
                      htmlFor="sign-up-org-step2-org-name-input"
                      className="my-auto font-medium text-sm text-cfSignInSignUpInputLabelText text-left after:content-['*'] after:text-red-500"
                    >
                      {t("homeAuthPages:your_org_name")}
                    </label>
                    <input
                      type="text"
                      name="org_name"
                      id="sign-up-org-step2-org-name-input"
                      placeholder={t("homeAuthPages:org_name")}
                      className="rounded-md w-full h-12 focus:outline-none border border-cfSignInSignUpInputBorder text-base text-cfSignInSignUpInputPlaceholderText"
                      style={{ borderRadius: InputBorderRadius }}
                      maxLength={ORG_SIGN_UP_FIELD_LIMITS.orgName}
                      value={values.org_name}
                      onChange={(e) =>
                        setFieldValue(
                          "org_name",
                          sanitizeOrganisationNameInput(e.target.value),
                        )
                      }
                    />
                  </div>
                  <p
                    id="sign-up-org-step2-org-name-error-message"
                    className="text-cfSignInSignUpInputPlaceholderText text-left text-red-600"
                  >
                    {errors.org_name &&
                      touched.org_name &&
                      `${errors.org_name}`}
                  </p>

                  {/* Country Dropdown */}
                  <div className="flex flex-col gap-4 rounded-md bg-inherit w-full h-[${InputContainerHeight}]">
                    <label
                      htmlFor="sign-up-org-step2-state-select"
                      className="my-auto font-medium text-sm text-cfSignInSignUpInputLabelText text-left after:content-['*'] after:text-red-500"
                    >
                      {t("homeAuthPages:select_state")}
                    </label>
                    <select
                      name="state"
                      id="sign-up-org-step2-state-select"
                      className="rounded-md w-full h-12 focus:outline-none border border-cfSignInSignUpInputBorder text-base text-cfSignInSignUpInputPlaceholderText"
                      value={values.state}
                      onChange={handleChange}
                    >
                      <option value="">
                        {t("homeAuthPages:select_state")}
                      </option>
                      {STATES_IN_INDIA.map((state) => (
                        <option value={state}>{state}</option>
                      ))}
                    </select>
                  </div>
                  <p
                    id="sign-up-org-step2-state-error-message"
                    className="text-cfSignInSignUpInputPlaceholderText text-left text-red-600"
                  >
                    {errors.state && touched.state && `${errors.state}`}
                  </p>

                  {/* GSTIN Field */}
                  <div className="flex flex-col gap-4 rounded-md bg-inherit w-full h-[${InputContainerHeight}]">
                    <label
                      htmlFor="sign-up-org-step2-gstin-input"
                      className="my-auto font-medium text-sm text-cfSignInSignUpInputLabelText text-left"
                    >
                      {t("homeAuthPages:gstin_optional")}
                    </label>
                    <input
                      type="text"
                      name="gstin"
                      id="sign-up-org-step2-gstin-input"
                      placeholder={t("homeAuthPages:gstin")}
                      className="rounded-md w-full h-12 focus:outline-none border border-cfSignInSignUpInputBorder text-base text-cfSignInSignUpInputPlaceholderText"
                      style={{ borderRadius: InputBorderRadius }}
                      maxLength={ORG_SIGN_UP_FIELD_LIMITS.gstin}
                      value={values.gstin || ""}
                      onChange={(e) =>
                        setFieldValue(
                          "gstin",
                          sanitizeGstinInput(e.target.value),
                        )
                      }
                    />
                  </div>
                  <p
                    id="sign-up-org-step2-gstin-error-message"
                    className="text-cfSignInSignUpInputPlaceholderText text-left text-red-600"
                  >
                    {errors.gstin && touched.gstin && `${errors.gstin}`}
                  </p>

                  {/* DOCUMENT UPLOAD */}

                  {/* Company Inc. Certificate Document Upload */}
                  {/* <div className="flex flex-col gap-4 rounded-md bg-inherit w-full">
                    <label
                      htmlFor="incorporation_certificate"
                      className="my-auto font-medium text-sm text-cfSignInSignUpInputLabelText text-left"
                    >
                      Company Inc. Certificate(optional)
                    </label>
                    <SupportingDocumentUpload
                      id="incorporation_certificate"
                      name="incorporation_certificate"
                      altText="Upload Company Inc. Certificate"
                      onFileUpload={(event) => {
                        const file = event.target.files[0];
                        if (file) {
                          setFieldValue("incorporation_certificate", file);
                          validateField("incorporation_certificate");
                        }
                      }}
                      docHeading="Upload"
                      docSubHeading="your Company Inc. certificate"
                    />
                    <p className="text-cfSignInSignUpInputPlaceholderText text-left text-red-600">
                      {errors.incorporation_certificate &&
                        touched.incorporation_certificate &&
                        `${errors.incorporation_certificate}`}
                    </p>
                  </div> */}
                  {/* GST Certificate Document Upload */}
                  {/* <div className="flex flex-col gap-4 rounded-md bg-inherit w-full">
                    <label
                      htmlFor="gst_certificate"
                      className="my-auto font-medium text-sm text-cfSignInSignUpInputLabelText text-left"
                    >
                      GST Certificate(optional)
                    </label>
                    <SupportingDocumentUpload
                      id="gst_certificate"
                      name="gst_certificate"
                      altText="Upload GST Certificate"
                      onFileUpload={(event) => {
                        const file = event.target.files[0];
                        if (file) {
                          setFieldValue("gst_certificate", file);
                          validateField("gst_certificate");
                        }
                      }}
                      docHeading="Upload"
                      docSubHeading="your GST certificate"
                    />
                    <p className="text-cfSignInSignUpInputPlaceholderText text-left text-red-600">
                      {errors.gst_certificate &&
                        touched.gst_certificate &&
                        `${errors.gst_certificate}`}
                    </p>
                  </div> */}
                </div>
                <div className="flex custom-recaptcha-width justify-center items-center mt-4">
                  <ReCAPTCHA
                    ref={createOrgRecaptchaRef}
                    sitekey={AppConstants.GOOGLE_CAPTCHA_SITE_KEY}
                    onChange={handleCaptchaChange}
                  />
                </div>
                <div className="w-full my-3 flex flex-col gap-[0.875rem]">
                  <div>
                    <input
                      id="sign-up-org-step2-create-button"
                      type="submit"
                      value={t("homeAuthPages:create")}
                      disabled={
                        !values.org_name?.trim() ||
                        !values.state?.trim() ||
                        !captchaValue
                      }
                      className="rounded-xl text-base font-semibold bg-cfButton hover:bg-cfPrimaryDark py-1 px-5 text-cfSecondary w-full h-11 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-cfButton"
                    />
                  </div>
                </div>

                <div className="flex justify-center text-[1rem] leading-[1.2rem] mt-4">
                  <p className="text-cfDontHaveAccountText">
                    {t("homeAuthPages:already_have_account")}
                  </p>
                  &nbsp;
                  <button
                    id="sign-up-org-step2-sign-in-navigation-button"
                    onClick={handleToggleComponent}
                    className="flex focus:outline-none"
                  >
                    <p className="hover:text-cfPrimaryDark font-semibold text-cfButton">
                      {t("homeAuthPages:sign_in")}
                    </p>
                  </button>
                </div>
              </div>
            </Form>
          )}
        </Formik>
        {isSignUp && <SignUpInModalLoader />}
        {showErrorModal && (
          <ErrorSignUpInNotification
            closeModal={handleCloseErrorModal}
            error={errorDetails?.error}
            message={errorDetails?.message}
          />
        )}
      </>
    );
  } else if (message && orgSignUpState === 3) {
    return (
      <div className="w-full px-8 py-6 max-w-[28rem] min-h-[28rem] flex flex-col justify-between">
        <div className="h-full flex flex-col gap-4">
          <img
            src={CircleGreenTickIcon}
            alt="Process started for account creation"
            className="w-20 mx-auto"
          />

          <div className={`bg-sky-200 rounded-md p-2 text-center shadow-md`}>
            {message}
          </div>
        </div>

        <div className="flex justify-center mb-2">
          <button
            id="sign-up-org-success-sign-in-button"
            onClick={handleToggleComponent}
            className="rounded-xl text-base font-semibold bg-cfButton hover:bg-cfPrimaryDark py-1 px-5 text-cfSecondary w-full h-11 focus:outline-none"
          >
            {t("homeAuthPages:sign_in")}
          </button>
        </div>

        {showErrorModal && (
          <ErrorSignUpInNotification
            closeModal={handleCloseErrorModal}
            error={errorDetails?.error}
            message={errorDetails?.message}
          />
        )}
      </div>
    );
  } else if (!basicSignUpMessage && message && !isSignOrg) {
    return (
      <div className="w-full px-8 py-6">
        <div className={`bg-sky-200 rounded-md p-2 text-center shadow-md`}>
          {message}
        </div>
        <div className="flex justify-center text-[1rem] leading-[1.2rem] mb-2">
          <button
            id="sign-up-individual-success-sign-in-button"
            onClick={handleToggleComponent}
            className="flex m-3 bg-cfPrimaryLight hover:bg-cfPrimaryLight rounded-lg text-cfSecondary px-2 py-1 focus:outline-none"
          >
            <p>{t("homeAuthPages:sign_in")}</p>
          </button>
          {showErrorModal && (
            <ErrorSignUpInNotification
              closeModal={handleCloseErrorModal}
              error={errorDetails?.error}
              message={errorDetails?.message}
            />
          )}
        </div>
      </div>
    );
  }
};

export default SignUpSideCurtains;
